import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ShieldCheck, Lock, Eye, EyeOff, KeyRound, ArrowLeft } from 'lucide-react';
import { isBlockedEmailProvider } from '../utils/isBlockedEmailProvider';
import { toast } from 'sonner';
import { DesktopDownloadButton } from '../components/DesktopDownloadButton';
import { RecoveryKeyDialog } from '../components/RecoveryKeyDialog';
import { GovernmentDisclaimer } from '../components/GovernmentDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back to Government Shield');
      navigate('/app');
    } catch (error) {
      // Backend returns 403 with `EMAIL_NOT_VERIFIED` when a user tries
      // to log in before clicking the verification link. Route them to
      // the "Check your email" landing so they can resend.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && detail === 'EMAIL_NOT_VERIFIED') {
        toast.message('Your email isn\'t verified yet — we\'ve re-opened the check-email page.');
        navigate(`/check-email?email=${encodeURIComponent(email)}`);
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ backgroundColor: '#05050A' }}>
      {/* Visual Side */}
      <div className="hidden lg:flex flex-col justify-center items-center relative overflow-hidden" style={{ backgroundColor: '#0F1115' }}>
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative z-10 p-12 text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-8 glow-primary">
            <ShieldCheck className="w-10 h-10 text-primary" strokeWidth={1.5} />
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Government Shield
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            End-to-end encrypted communications. Your messages, your privacy, your control.
          </p>
          <div className="mt-12 flex items-center justify-center gap-8 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>E2E Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 glow-success" />
              <span>24h Auto-Destruct</span>
            </div>
          </div>
          <div className="mt-8 flex justify-center">
            <DesktopDownloadButton variant="pill" />
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="flex flex-col items-center justify-center p-8 sm:p-12">
        <Card className="w-full max-w-md bg-card/50 border-border backdrop-blur-sm">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-3 mb-4">
              <ShieldCheck className="w-8 h-8 text-primary" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Government Shield</span>
            </div>
            <CardTitle className="text-2xl font-semibold tracking-tight">Sign in</CardTitle>
            <CardDescription className="text-slate-400">
              Enter your credentials to access your secure conversations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="bg-background/50 border-input focus:border-primary"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="bg-background/50 border-input focus:border-primary pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full glow-primary"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-slate-400 hover:text-primary"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
            <div className="mt-6 text-center text-sm text-slate-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                Create one
              </Link>
            </div>
            <div className="mt-2 text-center text-xs text-slate-500">
              No invite yet?{' '}
              <Link to="/waitlist" className="text-primary/80 hover:underline" data-testid="waitlist-link">
                Request access
              </Link>
              {' · '}
              <Link to="/selfhost" className="text-slate-500 hover:text-slate-300 hover:underline" data-testid="selfhost-link">
                Self-host
              </Link>
              {' · '}
              <Link to="/manifesto" className="text-slate-500 hover:text-slate-300 hover:underline" data-testid="manifesto-link">
                Why Government Shield
              </Link>
              {' · '}
              <Link to="/legal" className="text-slate-500 hover:text-slate-300 hover:underline" data-testid="legal-link">
                Terms & Privacy
              </Link>
            </div>
          </CardContent>
        </Card>
        <GovernmentDisclaimer className="mt-6 max-w-md w-full" />
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState(null);
  const [registeredUsername, setRegisteredUsername] = useState('');
  const [pendingSession, setPendingSession] = useState(null);
  // Detect fresh-install: when has_owner is false, this registration is
  // the first-user-becomes-owner bootstrap for a self-hosted deployment.
  const [systemStatus, setSystemStatus] = useState(null);
  const { register, activateSessionFromRegister } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/system/status`);
        setSystemStatus(r.data);
      } catch {
        // Soft-fail — the register flow still works, we just won't show
        // the "you'll be the admin" banner.
      }
    })();
  }, []);

  const isFirstUser = systemStatus && systemStatus.has_owner === false;

  // Real-time check: is the currently-typed email on a blocked
  // Microsoft consumer host? When true, escalate the static amber
  // notice into a red error and disable submit so the user gets the
  // signal before the round-trip to the server.
  const emailBlocked = isBlockedEmailProvider(email);

  // Pre-fill the invite code from a ?invite= query param so friends
  // clicking a shared link have the field populated automatically.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('invite') || params.get('code');
    if (q) setInviteCode(q.trim().toUpperCase());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!isFirstUser && !inviteCode.trim()) {
      toast.error('Invite code is required');
      return;
    }
    setLoading(true);
    try {
      const { recoveryKey: rk, accessToken, user: userData } = await register(
        username, email, password, inviteCode.trim() || undefined
      );
      if (userData?.is_owner) {
        toast.success("You're now the server owner. Save your recovery key.", {
          duration: 6000,
        });
      } else {
        toast.success('Account created! Save your recovery key.');
      }
      setRegisteredUsername(username);
      setPendingSession({ accessToken, user: userData });
      setRecoveryKey(rk);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeRecoveryKey = async () => {
    if (pendingSession) {
      await activateSessionFromRegister(pendingSession.accessToken, pendingSession.user);
    }
    setRecoveryKey(null);
    // If the server marked this user unverified (new gating), we drop
    // them on the "check your email" landing instead of the dashboard.
    // Backend-issued JWT is still stored (so the user's public key gets
    // uploaded during activation), but login on another device will
    // 403 until they click the email link.
    if (pendingSession?.user && pendingSession.user.email_verified === false) {
      navigate(`/check-email?email=${encodeURIComponent(pendingSession.user.email)}`);
      return;
    }
    navigate('/app');
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ backgroundColor: '#05050A' }}>
      {/* Visual Side */}
      <div className="hidden lg:flex flex-col justify-center items-center relative overflow-hidden" style={{ backgroundColor: '#0F1115' }}>
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative z-10 p-12 text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-8 glow-primary">
            <ShieldCheck className="w-10 h-10 text-primary" strokeWidth={1.5} />
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Join Government Shield
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            Create your secure identity. Communicate privately with those you trust.
          </p>
          <div className="mt-12 space-y-4 text-left">
            <div className="flex items-start gap-3 text-slate-400">
              <Lock className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-slate-200 font-medium">End-to-End Encryption</p>
                <p className="text-sm">Messages encrypted locally, only you and recipients can read</p>
              </div>
            </div>
            <div className="flex items-start gap-3 text-slate-400">
              <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-slate-200 font-medium">Invite-Only Contacts</p>
                <p className="text-sm">Control who can message you with invite codes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="flex flex-col items-center justify-center p-8 sm:p-12">
        <Card className="w-full max-w-md bg-card/50 border-border backdrop-blur-sm">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-3 mb-4">
              <ShieldCheck className="w-8 h-8 text-primary" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Government Shield</span>
            </div>
            <CardTitle className="text-2xl font-semibold tracking-tight">Create account</CardTitle>
            <CardDescription className="text-slate-400">
              Set up your secure communication identity
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isFirstUser && (
              <div
                className="mb-5 p-4 rounded-lg border border-primary/30 bg-primary/5 flex items-start gap-3"
                data-testid="first-user-admin-banner"
              >
                <ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" strokeWidth={1.75} />
                <div className="space-y-1">
                  <p className="text-sm text-white font-semibold">
                    You'll be the owner of this server
                  </p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    This deployment is fresh — whoever registers first becomes
                    the <span className="text-primary font-medium">server owner</span>.
                    You'll control who can join, audit referral chains, revoke
                    accounts, and appoint sub-admins. Ownership can't be
                    transferred or couped — it's yours for the life of this
                    deployment.
                  </p>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-300">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Choose a username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  data-testid="register-username-input"
                  className="bg-background/50 border-input focus:border-primary"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="register-email-input"
                  aria-invalid={emailBlocked || undefined}
                  className={`bg-background/50 border-input focus:border-primary ${
                    emailBlocked ? 'border-red-500/60 focus:border-red-500' : ''
                  }`}
                />
                <p
                  className={`text-[11px] leading-relaxed flex items-start gap-1.5 ${
                    emailBlocked ? 'text-red-400' : 'text-amber-400/80'
                  }`}
                  data-testid="register-email-provider-notice"
                  data-blocked={emailBlocked || undefined}
                  role={emailBlocked ? 'alert' : undefined}
                >
                  <span className={`font-bold mt-px ${emailBlocked ? 'text-red-400' : 'text-amber-400'}`}>
                    !
                  </span>
                  <span>
                    {emailBlocked ? (
                      <>
                        <span className="font-semibold">
                          This address won't work — Microsoft drops our verification mail.
                        </span>{' '}
                        Try Gmail, Proton Mail, Tutanota, Fastmail, or your work / university address.{' '}
                        <Link
                          to="/help/email-providers"
                          className="underline decoration-dotted hover:text-red-300 transition-colors"
                          data-testid="register-email-why-link"
                        >
                          Why?
                        </Link>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold">Outlook, Hotmail, Live, and MSN are not supported.</span>{' '}
                        Microsoft's inbound filters silently drop our verification mail. Use
                        Gmail, Proton Mail, Tutanota, Fastmail, or your work / university address.
                      </>
                    )}
                  </span>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="register-password-input"
                    className="bg-background/50 border-input focus:border-primary pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="register-confirm-password-input"
                  className="bg-background/50 border-input focus:border-primary"
                />
              </div>
              {!isFirstUser && (
                <div className="space-y-2">
                  <Label htmlFor="invite-code">Invite code</Label>
                  <Input
                    id="invite-code"
                    type="text"
                    placeholder="8-character code (e.g. A3F9B7K2)"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    required
                    maxLength={16}
                    autoComplete="off"
                    data-testid="register-invite-code-input"
                    className="bg-background/50 border-input focus:border-primary font-mono tracking-wider"
                  />
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Government Shield is invite-only. Don't have a code?{' '}
                    <Link to="/waitlist" className="text-primary hover:underline" data-testid="register-to-waitlist-link">
                      Join the waitlist
                    </Link>
                    .
                  </p>
                </div>
              )}
              <Button
                type="submit"
                className="w-full glow-primary"
                disabled={loading || emailBlocked}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-slate-400">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
        <GovernmentDisclaimer className="mt-6 max-w-md w-full" />
      </div>

      {recoveryKey && (
        <RecoveryKeyDialog
          isOpen={!!recoveryKey}
          recoveryKey={recoveryKey}
          username={registeredUsername}
          onAcknowledge={handleAcknowledgeRecoveryKey}
          context="register"
        />
      )}
    </div>
  );
};


// ============================================================================
// FORGOT PASSWORD (recovery-key based reset)
// ============================================================================

export const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: email+key, 2: new password, 3: show new key
  const [email, setEmail] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newRecoveryKey, setNewRecoveryKey] = useState(null);
  const [pendingSession, setPendingSession] = useState(null); // {accessToken, user}
  const { resetPassword, activateSession } = useAuth();
  const navigate = useNavigate();

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email.trim() || !recoveryKey.trim()) {
      toast.error('Please enter your email and recovery key');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-recovery-key`, {
        email: email.trim(),
        recovery_key: recoveryKey.trim(),
      });
      if (res.data.valid) {
        setStep(2);
      } else {
        toast.error('Invalid email or recovery key');
      }
    } catch (err) {
      toast.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const { recoveryKey: rk, accessToken, user: userData } = await resetPassword(email.trim(), recoveryKey.trim(), newPassword);
      toast.success('Password reset successfully');
      setPendingSession({ accessToken, user: userData });
      setNewRecoveryKey(rk);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. Please verify your details.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeNewKey = async () => {
    // Only now do we activate the session — this will trigger PublicRoute redirect to /.
    if (pendingSession) {
      await activateSession(pendingSession.accessToken, pendingSession.user);
    }
    setNewRecoveryKey(null);
    navigate('/app');
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ backgroundColor: '#05050A' }}>
      {/* Visual Side */}
      <div className="hidden lg:flex flex-col justify-center items-center relative overflow-hidden" style={{ backgroundColor: '#0F1115' }}>
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative z-10 p-12 text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-amber-500/10 mb-8">
            <KeyRound className="w-10 h-10 text-amber-400" strokeWidth={1.5} />
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Recover Access
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            Use the 64-character recovery key we gave you when you signed up. It's the only way in —
            Government Shield has no password-reset email.
          </p>
          <div className="mt-12 space-y-3 text-left">
            <div className="flex items-start gap-3 text-slate-400">
              <Lock className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm">Your old key becomes invalid after reset — you'll get a new one.</p>
            </div>
            <div className="flex items-start gap-3 text-slate-400">
              <ShieldCheck className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm">
                All existing sessions are signed out immediately for your security.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="flex flex-col items-center justify-center p-8 sm:p-12">
        <Card className="w-full max-w-md bg-card/50 border-border backdrop-blur-sm">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-3 mb-4">
              <KeyRound className="w-8 h-8 text-amber-400" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Government Shield</span>
            </div>
            <CardTitle className="text-2xl font-semibold tracking-tight">
              {step === 1 ? 'Verify recovery key' : 'Set new password'}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {step === 1
                ? 'Enter your email and recovery key to continue.'
                : 'Choose a strong password. You will be signed in automatically.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 && (
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fp-email" className="text-slate-300">Email</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className="bg-background/50 border-input focus:border-primary"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fp-key" className="text-slate-300">Recovery Key</Label>
                  <Input
                    id="fp-key"
                    type="text"
                    placeholder="AAAA-BBBB-CCCC-DDDD-EEEE-FFFF-0000-1111"
                    value={recoveryKey}
                    onChange={(e) => setRecoveryKey(e.target.value)}
                    required
                    data-testid="forgot-recovery-key-input"
                    className="bg-background/50 border-input focus:border-primary font-mono text-xs"
                    autoComplete="off"
                    spellCheck="false"
                  />
                  <p className="text-xs text-slate-500">
                    Paste the 64-character key from the .txt file you saved during signup. Dashes
                    and case don't matter.
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full glow-primary"
                  disabled={loading}
                  data-testid="forgot-verify-btn"
                >
                  {loading ? 'Verifying...' : 'Verify key'}
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fp-new-pw" className="text-slate-300">New Password</Label>
                  <div className="relative">
                    <Input
                      id="fp-new-pw"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      data-testid="forgot-new-password-input"
                      className="bg-background/50 border-input focus:border-primary pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fp-confirm-pw" className="text-slate-300">Confirm Password</Label>
                  <Input
                    id="fp-confirm-pw"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="forgot-confirm-password-input"
                    className="bg-background/50 border-input focus:border-primary"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full glow-primary"
                  disabled={loading}
                  data-testid="forgot-reset-btn"
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center text-sm text-slate-400">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-primary hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>
        <GovernmentDisclaimer className="mt-6 max-w-md w-full" />
      </div>

      {newRecoveryKey && (
        <RecoveryKeyDialog
          isOpen={!!newRecoveryKey}
          recoveryKey={newRecoveryKey}
          username={email}
          onAcknowledge={handleAcknowledgeNewKey}
          context="reset"
        />
      )}
    </div>
  );
};
